import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (setInView(true), io.disconnect())),
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, inView];
}

const reveal = (inView, delay = 0) => ({
  opacity: inView ? 1 : 0,
  transform: inView ? "translateY(0)" : "translateY(12px)",
  transition: `opacity 480ms ease ${delay}ms, transform 480ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
});

const body = "rgba(26,26,25,0.78)";

export default function SizingFit() {
  const [ref, inView] = useInView();

  return (
    <main
      ref={ref}
      className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 pt-20 sm:pt-28 pb-24 sm:pb-32"
      data-testid="sizing-fit-page"
    >
      <div className="max-w-[820px]">
        <h1
          className="font-display leading-[1.05]"
          style={{
            fontSize: "clamp(2rem, 4vw, 3.25rem)",
            letterSpacing: "-0.005em",
            fontWeight: 400,
            ...reveal(inView, 0),
          }}
          data-testid="sizing-heading"
        >
          Sizing <span className="italic">& Fit</span>
        </h1>
        <p
          className="mt-10 sm:mt-12 text-base sm:text-[17px] leading-[1.85]"
          style={{ color: body, ...reveal(inView, 160), maxWidth: "600px" }}
          data-testid="sizing-intro"
        >
          Our pieces are cut with intention. Each silhouette has been considered carefully to flatter naturally while allowing ease of movement.
        </p>
      </div>

      <div className="mt-20 sm:mt-24 space-y-12 sm:space-y-14 max-w-[640px]" style={reveal(inView, 280)}>
        <section className="border-t pt-12 sm:pt-14" style={{ borderColor: "rgba(26,26,25,0.10)" }}>
          <h2
            className="font-display tracking-[0.22em] uppercase"
            style={{ fontSize: "clamp(0.95rem, 1.1vw, 1.05rem)", fontWeight: 500, color: "#0E0E0D" }}
          >
            Size Range
          </h2>
          <p className="mt-6 text-base sm:text-[16.5px] leading-[1.95]" style={{ color: body }}>
            The Signature Mini is available in XS, S, M, L, and XL. Please refer to the size
            chart on each product page for detailed measurements.
          </p>
        </section>

        <section className="border-t pt-12 sm:pt-14" style={{ borderColor: "rgba(26,26,25,0.10)" }}>
          <h2
            className="font-display tracking-[0.22em] uppercase"
            style={{ fontSize: "clamp(0.95rem, 1.1vw, 1.05rem)", fontWeight: 500, color: "#0E0E0D" }}
          >
            Fit Notes
          </h2>
          <p className="mt-6 text-base sm:text-[16.5px] leading-[1.95]" style={{ color: body }}>
            The mini sits at the natural waist with a clean, tailored line. If you are between
            sizes, we recommend selecting the larger size for a more relaxed fit.
          </p>
        </section>

        <section className="border-t pt-12 sm:pt-14" style={{ borderColor: "rgba(26,26,25,0.10)" }}>
          <h2
            className="font-display tracking-[0.22em] uppercase"
            style={{ fontSize: "clamp(0.95rem, 1.1vw, 1.05rem)", fontWeight: 500, color: "#0E0E0D" }}
          >
            Need Help?
          </h2>
          <p className="mt-6 text-base sm:text-[16.5px] leading-[1.95]" style={{ color: body }}>
            For personalised sizing assistance, please write to{" "}
            <Link to="/contact" className="underline underline-offset-4 hover:opacity-70" style={{ color: "#0E0E0D" }}>
              our team
            </Link>{" "}
            and we will be delighted to help.
          </p>
        </section>
      </div>
    </main>
  );
}
